/**
 * 设定HTTP API服务
 *
 * 封装设定相关的REST API调用，用于设定CRUD操作。
 * 使用 HttpOnly Cookie 进行认证，浏览器自动携带 Token。
 */

import type { Setting, SettingCreate, SettingUpdate } from '../types/setting';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

export class HttpError extends Error {
  constructor(
    message: string,
    public status: number,
    public code?: string
  ) {
    super(message);
    this.name = 'HttpError';
  }
}

export class UnauthorizedError extends HttpError {
  constructor(message: string = '未授权，请重新登录') {
    super(message, 401, 'UNAUTHORIZED');
    this.name = 'UnauthorizedError';
  }
}

export class NotFoundError extends HttpError {
  constructor(message: string = '资源不存在') {
    super(message, 404, 'NOT_FOUND');
    this.name = 'NotFoundError';
  }
}

export class ForbiddenError extends HttpError {
  constructor(message: string = '没有权限执行此操作') {
    super(message, 403, 'FORBIDDEN');
    this.name = 'ForbiddenError';
  }
}

interface SettingListResponse {
  settings: Setting[];
  total: number;
}

interface SettingTokenUsage {
  total_tokens: number;
  total_settings: number;
  available_tokens: number;
}

function getHeaders(): HeadersInit {
  return {
    'Content-Type': 'application/json',
  };
}

async function handleResponseError(response: Response): Promise<never> {
  let errorMessage = '请求失败';

  try {
    const data = await response.json();
    errorMessage = data.detail || data.message || errorMessage;
  } catch {
  }

  switch (response.status) {
    case 401:
      throw new UnauthorizedError(errorMessage);
    case 403:
      throw new ForbiddenError(errorMessage);
    case 404:
      throw new NotFoundError(errorMessage);
    default:
      throw new HttpError(errorMessage, response.status);
  }
}

export class SettingApiService {
  private baseUrl: string;

  constructor(baseUrl: string = API_BASE_URL) {
    this.baseUrl = baseUrl;
  }

  async listSettings(workspaceId: number): Promise<Setting[]> {
    const response = await fetch(
      `${this.baseUrl}/api/settings/workspaces/${workspaceId}`,
      {
        method: 'GET',
        headers: getHeaders(),
        credentials: 'include',
      }
    );

    if (!response.ok) {
      await handleResponseError(response);
    }

    const data: SettingListResponse = await response.json();
    return data.settings;
  }

  async createSetting(workspaceId: number, data: SettingCreate): Promise<Setting> {
    const response = await fetch(
      `${this.baseUrl}/api/settings/workspaces/${workspaceId}`,
      {
        method: 'POST',
        headers: getHeaders(),
        credentials: 'include',
        body: JSON.stringify(data),
      }
    );

    if (!response.ok) {
      await handleResponseError(response);
    }

    return await response.json();
  }

  async getSetting(settingId: number): Promise<Setting> {
    const response = await fetch(
      `${this.baseUrl}/api/settings/${settingId}`,
      {
        method: 'GET',
        headers: getHeaders(),
        credentials: 'include',
      }
    );

    if (!response.ok) {
      await handleResponseError(response);
    }

    return await response.json();
  }

  async updateSetting(settingId: number, data: SettingUpdate): Promise<Setting> {
    const response = await fetch(
      `${this.baseUrl}/api/settings/${settingId}`,
      {
        method: 'PUT',
        headers: getHeaders(),
        credentials: 'include',
        body: JSON.stringify(data),
      }
    );

    if (!response.ok) {
      await handleResponseError(response);
    }

    return await response.json();
  }

  async deleteSetting(settingId: number): Promise<void> {
    const response = await fetch(
      `${this.baseUrl}/api/settings/${settingId}`,
      {
        method: 'DELETE',
        headers: getHeaders(),
        credentials: 'include',
      }
    );

    if (!response.ok) {
      await handleResponseError(response);
    }
  }

  async lockSetting(settingId: number, action: 'lock' | 'unlock'): Promise<Setting> {
    const response = await fetch(
      `${this.baseUrl}/api/settings/${settingId}/lock`,
      {
        method: 'POST',
        headers: getHeaders(),
        credentials: 'include',
        body: JSON.stringify({ action }),
      }
    );

    if (!response.ok) {
      await handleResponseError(response);
    }

    return await response.json();
  }

  async searchSettings(workspaceId: number, names: string[]): Promise<Setting[]> {
    const params = new URLSearchParams({ names: names.join(',') });
    const response = await fetch(
      `${this.baseUrl}/api/settings/workspaces/${workspaceId}/search?${params}`,
      {
        method: 'GET',
        headers: getHeaders(),
        credentials: 'include',
      }
    );

    if (!response.ok) {
      await handleResponseError(response);
    }

    const data: SettingListResponse = await response.json();
    return data.settings;
  }

  async getDefaultModel(): Promise<string> {
    const response = await fetch(
      `${this.baseUrl}/api/settings/models/default`,
      {
        method: 'GET',
        headers: getHeaders(),
        credentials: 'include',
      }
    );

    if (!response.ok) {
      await handleResponseError(response);
    }

    const data = await response.json();
    return data.model;
  }

  async getTokenUsage(workspaceId: number): Promise<{
    total_tokens: number;
    total_settings: number;
    available_tokens: number;
  }> {
    const response = await fetch(
      `${this.baseUrl}/api/settings/workspaces/${workspaceId}/token-usage`,
      {
        method: 'GET',
        headers: getHeaders(),
        credentials: 'include',
      }
    );

    if (!response.ok) {
      await handleResponseError(response);
    }

    return await response.json();
  }
}

export const settingApi = new SettingApiService();

export default settingApi;
